/**
 * SPA platform implementation
 * Used when running in Vite SPA environment (React Router)
 */

import { stripBasePath } from "@app/lib/compta/base_path";
import type {
  AppRouter,
  RouterEvents,
  RouterEventType,
  TransitionOptions,
  UrlObject,
} from "@dust-tt/front/lib/platform/types";
import { ReactRouterLinkWrapper } from "@spa/lib/ReactRouterLinkWrapper";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useBlocker,
  useLocation,
  useNavigate,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from "react-router-dom";

/**
 * Safe wrapper around useNavigate that handles the case where
 * we're not inside a Router context (can happen during production build initialization)
 */
function useSafeNavigate() {
  try {
    // oxlint-disable-next-line react/rules-of-hooks -- Intentional try/catch wrapper for Router context safety.
    return useNavigate();
  } catch {
    return null;
  }
}

/**
 * Safe wrapper around useLocation that handles the case where
 * we're not inside a Router context
 */
function useSafeLocation() {
  try {
    // oxlint-disable-next-line react/rules-of-hooks -- Intentional try/catch wrapper for Router context safety.
    return useLocation();
  } catch {
    return null;
  }
}

/**
 * Safe wrapper around useSearchParam that handles the case where
 * we're not inside a Router context
 */
function useSafeSearchParams() {
  try {
    // oxlint-disable-next-line react/rules-of-hooks -- Intentional try/catch wrapper for Router context safety.
    return useRouterSearchParams();
  } catch {
    return [new URLSearchParams(window.location.search), () => {}] as const;
  }
}

/**
 * Convert URL object to string
 */
function urlToString(url: string | UrlObject): string {
  if (typeof url === "string") {
    return url;
  }
  let result = url.pathname ?? "";
  if (url.query && Object.keys(url.query).length > 0) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(url.query)) {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, v));
        } else {
          params.append(key, value);
        }
      }
    }
    const queryString = params.toString();
    if (queryString) {
      result += "?" + queryString;
    }
  }
  if (url.hash) {
    result += url.hash.startsWith("#") ? url.hash : `#${url.hash}`;
  }
  return result;
}

/**
 * Global event emitter for router events in SPA
 * This mimics Next.js router events to work with NavigationLoadingContext
 */
type RouterEventHandler = (url: string) => void;

const routerEventListeners: Map<string, Set<RouterEventHandler>> = new Map();

function createRouterEvents(): RouterEvents {
  return {
    on: (event: RouterEventType, handler: RouterEventHandler) => {
      if (!routerEventListeners.has(event)) {
        routerEventListeners.set(event, new Set());
      }
      routerEventListeners.get(event)!.add(handler);
    },
    off: (event: RouterEventType, handler: RouterEventHandler) => {
      routerEventListeners.get(event)?.delete(handler);
    },
    emit: (event: RouterEventType, ...args: unknown[]) => {
      routerEventListeners
        .get(event)
        ?.forEach((handler) => handler(String(args[0] ?? "")));
    },
  };
}

// Singleton events object so all useAppRouter calls share the same listeners
export const routerEvents = createRouterEvents();

export function useAppRouter(): AppRouter {
  const navigate = useSafeNavigate();
  const location = useSafeLocation();
  const [searchParams] = useSafeSearchParams();

  // Force re-render when location changes via window (fallback mode)
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    if (!location) {
      // We're in fallback mode, listen to popstate to update
      const handlePopState = () => setForceUpdate((n) => n + 1);
      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    }
  }, [location]);

  const push = useCallback(
    async (
      url: string | UrlObject,
      _as?: string,
      _options?: TransitionOptions
    ) => {
      // Sous-plan 5 : sous une base (VITE_BASE_PATH), un chemin qui la porte deja (construit
      // depuis window.location.pathname) ne doit pas la recevoir une seconde fois du routeur.
      const urlString = stripBasePath(
        urlToString(url),
        import.meta.env?.VITE_BASE_PATH ?? "/"
      );
      if (
        navigate &&
        !urlString.startsWith("http://") &&
        !urlString.startsWith("https://")
      ) {
        await navigate(urlString);
      } else {
        // Fallback to window.location if not in Router context
        window.location.href = urlString;
      }
      return true;
    },
    [navigate]
  );

  const replace = useCallback(
    async (
      url: string | UrlObject,
      _as?: string,
      _options?: TransitionOptions
    ) => {
      // Sous-plan 5 : sous une base (VITE_BASE_PATH), un chemin qui la porte deja (construit
      // depuis window.location.pathname) ne doit pas la recevoir une seconde fois du routeur.
      const urlString = stripBasePath(
        urlToString(url),
        import.meta.env?.VITE_BASE_PATH ?? "/"
      );
      if (
        navigate &&
        !urlString.startsWith("http://") &&
        !urlString.startsWith("https://")
      ) {
        await navigate(urlString, { replace: true });
      } else {
        // Fallback to window.location if not in Router context
        window.location.replace(urlString);
      }
      return true;
    },
    [navigate]
  );

  const back = useCallback(async () => {
    if (navigate) {
      await navigate(-1);
    } else {
      window.history.back();
    }
  }, [navigate]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  const query = useMemo(() => {
    const result: Record<string, string | string[] | undefined> = {};
    searchParams.forEach((value: string, key: string) => {
      const existing = result[key];
      if (existing !== undefined) {
        if (Array.isArray(existing)) {
          existing.push(value);
        } else {
          result[key] = [existing, value];
        }
      } else {
        result[key] = value;
      }
    });
    return result;
  }, [searchParams]);

  const pathname = location?.pathname ?? window.location.pathname;
  const search = location?.search ?? window.location.search;
  // React Router doesn't track hash - always use window.location.hash
  const hash = window.location.hash;

  // In SPA mode, router is always ready (no SSR hydration needed)
  const isReady = true;

  return useMemo(
    () => ({
      push,
      replace,
      back,
      reload,
      pathname,
      asPath: pathname + search + hash,
      query,
      isReady,
      events: routerEvents,
    }),
    [push, replace, back, reload, pathname, search, hash, query]
  );
}

export const LinkWrapper = ReactRouterLinkWrapper;

/**
 * Hook to get route params in SPA
 * Returns route parameters from the URL (e.g., :wId, :aId)
 */
export function usePathParams(): Record<string, string | undefined> {
  try {
    // oxlint-disable-next-line react/rules-of-hooks -- Intentional try/catch wrapper for Router context safety.
    return useRouterParams();
  } catch {
    return {};
  }
}

/**
 * Hook to get a required route param
 * Throws an error if the param is missing
 */
export function usePathParam(name: string): string | null {
  const params = usePathParams();
  const value = params[name];
  return value ?? null;
}

/**
 * Hook to get a required route param
 * Throws an error if the param is missing
 */
export function useRequiredPathParam(name: string): string {
  const params = usePathParams();
  const value = params[name];
  if (!value) {
    throw new Error(`Required route parameter "${name}" is missing`);
  }
  return value;
}

/**
 * Hook to get a search/query param in SPA
 * Returns the value of the specified query parameter or null
 */
export function useSearchParam(name: string): string | null {
  const [searchParams] = useSafeSearchParams();
  return searchParams.get(name);
}

/**
 * Navigation blocker for SPA using React Router's useBlocker.
 * Intercepts ALL navigation (browser back/forward, links, programmatic)
 * and calls onBlock to determine whether to proceed or cancel.
 */
export function useNavigationBlocker(
  shouldBlock: boolean,
  onBlock: () => Promise<boolean>
) {
  const blocker = useBlocker(shouldBlock);

  // Use a ref for onBlock to avoid re-triggering the blocked effect when the
  // callback reference changes. Only update the ref inside an effect.
  const onBlockRef = useRef(onBlock);
  useEffect(() => {
    onBlockRef.current = onBlock;
  }, [onBlock]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      void onBlockRef.current().then((proceed) => {
        if (proceed) {
          blocker.proceed?.();
        } else {
          blocker.reset?.();
        }
      });
    }
  }, [blocker]);
}

export type { AppRouter };
